/**
 * Convert an image File to a high-quality WebP data URL.
 * Keeps original dimensions; uses high WebP quality so visual fidelity is preserved
 * while typically shrinking file size (WebP compression).
 */

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image."));
    };
    img.src = url;
  });

const canvasToWebpDataUrl = (canvas, quality) =>
  new Promise((resolve, reject) => {
    try {
      // Prefer toBlob when available (more reliable size control)
      if (canvas.toBlob) {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("WebP encoding failed."));
              return;
            }
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error("Could not encode WebP."));
            reader.readAsDataURL(blob);
          },
          "image/webp",
          quality
        );
        return;
      }
      const dataUrl = canvas.toDataURL("image/webp", quality);
      if (!dataUrl || !dataUrl.startsWith("data:image/webp")) {
        reject(new Error("This browser cannot encode WebP."));
        return;
      }
      resolve(dataUrl);
    } catch (e) {
      reject(e);
    }
  });

const dataUrlByteLength = (dataUrl) => {
  const base64 = dataUrl.split(",")[1] || "";
  // base64 → approximate binary size
  return Math.floor((base64.length * 3) / 4);
};

/**
 * @param {File} file
 * @param {{ maxBytes?: number, quality?: number }} [options]
 * @returns {Promise<string>} WebP data URL (data:image/webp;base64,...)
 */
export async function optimizeImageToWebp(file, options = {}) {
  const maxBytes = options.maxBytes ?? 2 * 1024 * 1024;
  // High quality ≈ visually lossless for typical photos/screenshots
  let quality = options.quality ?? 0.95;

  if (!file?.type?.startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }

  const img = await loadImageFromFile(file);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (!w || !h) throw new Error("Invalid image dimensions.");

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is not available.");
  // Preserve transparency (important for PNG → WebP)
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);

  // Probe WebP support
  let dataUrl = await canvasToWebpDataUrl(canvas, quality);
  if (!String(dataUrl).startsWith("data:image/webp")) {
    throw new Error("This browser cannot encode WebP.");
  }

  // If over limit, lower quality slightly while staying high-fidelity
  const minQuality = 0.85;
  while (dataUrlByteLength(dataUrl) > maxBytes && quality > minQuality) {
    quality = Math.max(minQuality, quality - 0.05);
    dataUrl = await canvasToWebpDataUrl(canvas, quality);
  }

  if (dataUrlByteLength(dataUrl) > maxBytes) {
    throw new Error(
      `Image is still too large after WebP optimization (max ${Math.round(maxBytes / (1024 * 1024))} MB).`
    );
  }

  return dataUrl;
}
